import {
    totallyEmptyAccommodationQuantities,
    type AccommodationQuantities,
} from "@@/shared/acquire/accommodation-quantity";
import type { AccommodationQuantityType } from "@@/shared/acquire/accommodation-quantity-types";
import {
    accommodationQuantityTypeMeta,
    accommodationQuantityTypes,
    accommodationQuantityTypesCountedAsGuest,
} from "@@/shared/acquire/accommodation-quantity-types";
import type {
    Capacity,
    CapacityHash,
    CapacityWithEnforcedHash,
} from "@@/shared/acquire/resources/resource-accommodation/capacity";
import { capacityHashFactory } from "@@/shared/acquire/resources/resource-accommodation/capacity";
import type { ResourceAccommodation } from "@@/shared/acquire/resources/resource-accommodation/resource-accommodation";
import type { ResourceId } from "@@/shared/acquire/resources/resource-id";
import type {
    TranslatableString,
    UseTranslateResult,
    ValueTranslator,
} from "@@/shared/translations/translation";
import {
    enTranslator,
    svTranslator,
    translatableStringFactory,
} from "@@/shared/translations/translation";
import type { Branded } from "@@/utilities/branded";
import { exists } from "@@/utilities/exists";
import { generateId } from "@@/utilities/generate-id";
import { repeatIndex } from "@@/utilities/repeat";
import { sortAlphabeticallyAndNumerically, sortBy } from "@@/utilities/sort";
import { sum } from "@@/utilities/sum";

type ResourceAccommodationWithEnforcedCapacityHash = ResourceAccommodation & {
    capacity: Capacity & { hash: CapacityHash };
};

class QuantityDistributionError extends Error {
    public translatableMessage: TranslatableString;
    constructor(
        message: TranslatableString,
        public errorCode: string,
    ) {
        super(svTranslator(message));
        this.name = "QuantityDistributionError";
        this.translatableMessage = message;
    }
}

type VerificationCapacityId = Branded<string, "VerificationCapacityId">;
type VerificationCapacity = CapacityWithEnforcedHash & {
    _id: VerificationCapacityId;
};
type VerificationAndQuantityTypeId =
    `${VerificationCapacityId}_${AccommodationQuantityType}`;

type Distribution = {
    capacities: CapacityWithEnforcedHash[];
    distributionPerCapacityIndex: Map<number, AccommodationQuantities>;
};
/**
 * This function tries to distribute a requested quantities among
 * the given resource capacities and makes sure that it's possible
 * to distribute the quantities among the resources without any of them
 * exceeding or falling short of their capacities. \
 * If it's not possible to distribute the quantities, a `QuantityDistributionError` is thrown
 */
const _getDistribution = (params: {
    capacities: CapacityWithEnforcedHash[];
    requestedQuantity: AccommodationQuantities;
}): Distribution => {
    const { requestedQuantity } = params;

    // Give each capacity a unique identifier
    const capacities = params.capacities.map(capacity => {
        const cap: VerificationCapacity = {
            ...capacity,
            _id: generateId({
                prefix: "capacity__",
            }) as VerificationCapacityId,
        };
        return cap;
    });

    // Helper functions
    /**
     * A function that gets the quantity of for a quantity type of
     * a requested quantity. The quantity the user wants to book
     * for the given quantity type. if not defined it will return 0.
     */
    const getTotalRequestedQuantity = (
        quantityType: AccommodationQuantityType,
    ): number => {
        return requestedQuantity[quantityType] || 0;
    };

    /**
     * A function that gets the total capacity for a quantity type
     * of all resources combined. If no capacity is set for the quantity type
     * it will return undefined.
     */
    const _getTotalCapacity = (
        quantityType: AccommodationQuantityType,
        maxOrMin: "min" | "max",
    ): number | undefined => {
        if (
            capacities.every(
                capacity =>
                    typeof capacity[quantityType]?.[maxOrMin] === "undefined",
            )
        ) {
            return undefined;
        }
        return capacities.reduce(
            (acc, capacity) => acc + (capacity[quantityType]?.[maxOrMin] ?? 0),
            0,
        );
    };

    /**
     * Get the total minimum capacity for a quantity type
     * If not set, it will return undefined
     */
    const getTotalMinCapacity = (
        quantityType: AccommodationQuantityType,
    ): number | undefined => {
        return _getTotalCapacity(quantityType, "min");
    };
    /**
     * Get the total maximum capacity for a quantity type
     * If not set, it will return undefined
     */
    const getTotalMaxCapacity = (
        quantityType: AccommodationQuantityType,
    ): number | undefined => {
        return _getTotalCapacity(quantityType, "max");
    };

    // What's the main quantity type?
    // If there are any adults, we'll use that as the main quantity type
    // otherwise we'll use guests as the main quantity type
    const mainQuantityType =
        getTotalRequestedQuantity("ADULT") > 0 ? "ADULT" : "GUEST";

    // First we'll make sure it's possible to distribute the main quantity with at least one for
    // each requested resource
    // TODO: Should we require that all resources have at least one adult?
    // TODO: or should we really, as long as they are following the min and max capacities set for each resource
    // TODO: isn't that enough? If they don't require an adult for an accommodation, why should we require it?

    /** Helper function to create a unique key for each resource and quantity type in combination */
    const getCapacityAndQuantityTypeId = (
        capacityId: VerificationCapacityId,
        quantityType: AccommodationQuantityType,
    ): VerificationAndQuantityTypeId => `${capacityId}_${quantityType}`;

    // We'll keep track of the distribution of quantities and how many
    // that are left to distribute
    const distributed = new Map<VerificationAndQuantityTypeId, number>();
    const distributionPerCapacityIndex = new Map<
        number,
        AccommodationQuantities
    >();
    const undistributed = new Map<AccommodationQuantityType, number>();

    for (const quantityType of accommodationQuantityTypes) {
        if (mainQuantityType === "ADULT" && quantityType === "GUEST") continue;
        const meta = accommodationQuantityTypeMeta[quantityType];
        const requestedQuantity = getTotalRequestedQuantity(quantityType);
        const totalMinCapacity = getTotalMinCapacity(quantityType);
        const totalMaxCapacity = getTotalMaxCapacity(quantityType);

        // We'll keep track of the undistributed quantity for each quantity type
        undistributed.set(quantityType, requestedQuantity);

        // If requested quantity is 0 or undefined...
        if (!requestedQuantity) {
            // ...we make sure total required min capacity is
            // not a number larger than 0
            if (totalMinCapacity && totalMinCapacity > 0) {
                // console.log("TOO_FEW", {
                //     capacities,
                //     totalMinCapacity,
                //     totalMaxCapacity,
                // });
                // If it is, we won't be able to fulfill the booking
                throw new QuantityDistributionError(
                    translatableStringFactory({
                        sv: `Minsta antal ${svTranslator(meta.name.plural).toLocaleLowerCase()} som krävs för att fullfölja bokningen är ${totalMinCapacity}, antalet efterfrågade var 0`,
                        en: `Minimum number of ${enTranslator(meta.name.plural).toLocaleLowerCase()} required to fulfill the booking is ${totalMinCapacity}, the requested quantity was 0`,
                    }),
                    "TOO_FEW",
                );
            }
        }

        // If requested total quantity is larger than total max capacity
        // we won't be able to fulfill the booking either
        if (totalMaxCapacity && requestedQuantity > totalMaxCapacity) {
            // console.log("TOO_MANY", {
            //     capacities,
            //     totalMinCapacity,
            //     totalMaxCapacity,
            // });
            throw new QuantityDistributionError(
                translatableStringFactory({
                    sv: `Antal begärt att boka av ${svTranslator(meta.name.plural).toLocaleLowerCase()} är ${requestedQuantity}, men totalt går det max att boka ${totalMaxCapacity} för valda resurserna.`,
                    en: `Requested quantity of ${enTranslator(meta.name.plural).toLocaleLowerCase()} is ${requestedQuantity}, but total max capacity of selected resources is ${totalMaxCapacity}.`,
                }),
                "TOO_MANY",
            );
        }
    }

    // So, we know the total requested quantity for each quantity type
    // is between the total min and max capacity of the resources
    // Now we'll try to distribute the requested quantities
    // as best we can among the resources and see if it's possible
    // to distribute the quantities without exceeding any capacity

    // 1. For each capacity, we'll first distribute the required min quantity
    for (let index = 0; index < capacities.length; index++) {
        const capacity = capacities[index];
        for (const quantityType of accommodationQuantityTypes) {
            if (mainQuantityType === "ADULT" && quantityType === "GUEST")
                continue;
            const meta = accommodationQuantityTypeMeta[quantityType];
            const capacityAndQuantityTypeId = getCapacityAndQuantityTypeId(
                capacity._id,
                quantityType,
            );
            const minQuantity = capacity[quantityType]?.min ?? 0;
            const available = undistributed.get(quantityType) || 0;
            if (available < minQuantity) {
                throw new QuantityDistributionError(
                    translatableStringFactory({
                        sv: `Antal begärda ${svTranslator(meta.name.plural)} räcker inte till för att uppfylla kravet på minst ${minQuantity} ${svTranslator(meta.name.plural)} för alla resurser.`,
                        en: `Requested quantity of ${enTranslator(meta.name.plural)} is not enough to fulfill the required minimum of ${minQuantity} ${enTranslator(meta.name.plural)} for all resources.`,
                    }),
                    "TOO_FEW_GUESTS",
                );
            }

            distributed.set(capacityAndQuantityTypeId, minQuantity);
            undistributed.set(quantityType, available - minQuantity);
            // Store the distributed quantities for each capacity
            const distribution =
                distributionPerCapacityIndex.get(index) ||
                totallyEmptyAccommodationQuantities();
            distribution[quantityType] =
                distribution[quantityType] + minQuantity;
            distributionPerCapacityIndex.set(index, distribution);
        }
    }

    // 2. Then we'll distribute the remaining requested quantity up to the required max quantity
    for (const quantityType of accommodationQuantityTypes) {
        // For every quantity type, we'll try to distribute the remaining
        // requested quantity among the capacities equally
        let undistributedValue = undistributed.get(quantityType) || 0;
        /** Returns true if all capacities have reached their maximum capacity */
        const maxCapacityReached = capacities.every(capacity => {
            const resAndQuantityTypeId = getCapacityAndQuantityTypeId(
                capacity._id,
                quantityType,
            );
            const currentlyDistributed =
                distributed.get(resAndQuantityTypeId) || 0;
            const maxQuantity = capacity[quantityType]?.max ?? 0;
            const possibleToDistribute = maxQuantity - currentlyDistributed;

            return possibleToDistribute <= 0;
        });

        // When the quantity type is CHILD, we'll try to distribute the
        // children equally among the capacities like the others but also
        // take into account that they are counted as guests together with
        // the adults. So we'll try to calculate the equality together with
        // the distributed adults.
        const startAt = (() => {
            const isChild = quantityType === "CHILD";
            if (!isChild) return 0;
            if (capacities.length <= 1) return 0;
            // Find the first capacity with less adult distribution than the
            // others, if any, and start the child distribution from there
            const distributedAdults = repeatIndex(capacities.length).map(
                index => {
                    return distributionPerCapacityIndex.get(index)?.ADULT || 0;
                },
            );
            // Find the first capacity with less adults than the ones before
            for (let i = 1; i < distributedAdults.length; i++) {
                if (distributedAdults[i] < distributedAdults[i - 1]) {
                    return i;
                }
            }
            return 0;
        })();

        // While we have an undistributed quantity and not all capacities
        // have reached their max capacity we'll loop through the capacities
        // and distribute the quantities one by one
        while (!maxCapacityReached && undistributedValue > 0) {
            for (
                let index = startAt;
                index < capacities.length + startAt;
                index++
            ) {
                const workingIndex = index % capacities.length;
                const capacity = capacities[workingIndex];
                const resAndQuantityTypeId = getCapacityAndQuantityTypeId(
                    capacity._id,
                    quantityType,
                );
                const maxQuantity = capacity[quantityType]?.max ?? 0;
                const available = undistributedValue;
                if (available === 0) continue;
                if (maxQuantity === 0) continue;

                const currentlyDistributed =
                    distributed.get(resAndQuantityTypeId) || 0;
                const possibleToDistribute = maxQuantity - currentlyDistributed;
                const quantityToDistribute = 1;
                if (possibleToDistribute < quantityToDistribute) continue;

                undistributedValue -= quantityToDistribute;
                distributed.set(resAndQuantityTypeId, quantityToDistribute);
                undistributed.set(
                    quantityType,
                    available - quantityToDistribute,
                );
                // Store the distributed quantities for each capacity
                const distribution =
                    distributionPerCapacityIndex.get(workingIndex) ||
                    totallyEmptyAccommodationQuantities();
                distribution[quantityType] =
                    distribution[quantityType] + quantityToDistribute;
                distributionPerCapacityIndex.set(workingIndex, distribution);
            }
        }
    }

    // 3. If we haven't been able to distribute all the quantities, we'll throw an error
    const undistributedTotalQuantity = sum(Array.from(undistributed.values()));
    if (undistributedTotalQuantity > 0) {
        throw new QuantityDistributionError(
            translatableStringFactory({
                sv: `Kunde inte placera ut begärt antal på det antal resurser som önskades.`,
                en: `Could not distribute requested quantity among the desired number of resources.`,
            }),
            "TOO_FEW_RESOURCES",
        );
    }

    // 4. Make a special check if the main quantity type is not GUEST.
    // Make sure that the total amount of other quantities counted as guests are not
    // exceeding or falling short of any min or max value of capacity set for GUEST
    if (mainQuantityType !== "GUEST") {
        const totalMinCapacity = getTotalMinCapacity("GUEST");
        const totalMaxCapacity = getTotalMaxCapacity("GUEST");

        const requestedTotalGuestQuantity = sum(
            accommodationQuantityTypesCountedAsGuest.map(type => {
                return getTotalRequestedQuantity(type);
            }),
        );

        if (
            totalMinCapacity &&
            requestedTotalGuestQuantity < totalMinCapacity
        ) {
            throw new QuantityDistributionError(
                translatableStringFactory({
                    sv: "För litet antal gäster för att kunna fullfölja bokningen",
                    en: "Too few guests to fulfill the booking",
                }),
                "TOO_FEW_GUESTS",
            );
        }

        if (
            totalMaxCapacity &&
            requestedTotalGuestQuantity > totalMaxCapacity
        ) {
            throw new QuantityDistributionError(
                translatableStringFactory({
                    sv: "För många gäster för att kunna fullfölja bokningen, max antal gäster överskridet.",
                    en: "Too many guests to fulfill the booking, max number of guests exceeded.",
                }),
                "TOO_MANY_GUESTS",
            );
        }

        // Well, it's within it's parameters, so we're good to go
    }

    // throw new QuantityDistributionError(
    //     toTranslatableString({
    //         sv: "Kunde inte hitta någon matchande fördelning för begärt antal",
    //         en: "Could not find a matching distribution for requested quantity",
    //     }),
    //     "NO_MATCHING_RATE",
    // );

    // console.log(
    //     "DISTRIBUTION AMONG CAPACITIES IN ARRAY",
    //     distributionPerCapacityIndex,
    // );

    // If we've come this far, we've been able to distribute
    // the quantities and all is good and dandy :)
    return {
        capacities,
        distributionPerCapacityIndex,
    };
};

/**
 * Same as "verifyRequestedQuantitiesArePossibleToDistributeAmongResources" but doesn't throw
 * an error if it's not possible to distribute the quantities among the resources.
 * Instead it will return undefined.
 * If it works, it will return the distribution of quantities for the given capacities.
 */
const getDistribution = (
    params: Parameters<typeof _getDistribution>[0] & { doNotThrow?: boolean },
) => {
    const { doNotThrow, ...passOnParams } = params;
    // Get the distribution of chosen quantities
    // among given capacities
    // If it's not possible to distribute the quantities
    // we'll just let the error to be thrown
    if (!doNotThrow) return _getDistribution(passOnParams);

    try {
        // Get the distribution of chosen quantities
        // among given capacities
        return _getDistribution(passOnParams);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
        // It it's not possible to distribute the quantities
        // we'll return undefined
        return undefined;
    }
};

const noCombinations = {
    // allResources: [],
    // resourceCombinations: [],
    uniqueCapacityCombinations: [],
    numberOfTotalCombinations: 0,
    resourcesWithHashMapped: new Map<
        ResourceId,
        ResourceAccommodationWithEnforcedCapacityHash
    >(),
    capacitiesByHash: new Map<CapacityHash, CapacityWithEnforcedHash>(),
};

/** Makes sure the capacity has a hash value */
const enforceHashForCapacity = (
    capacity: Capacity,
): CapacityWithEnforcedHash => {
    if (!capacity.hash) {
        const hash = capacityHashFactory(capacity);
        capacity.hash = hash;
        return capacity as CapacityWithEnforcedHash;
    }
    return capacity as CapacityWithEnforcedHash;
};

/** Makes sure all capacities has a hash value */
const enforceHashForCapacities = (
    capacities: Capacity[],
): CapacityWithEnforcedHash[] => {
    return capacities.map(enforceHashForCapacity);
};

/**
 * Generates resources and makes sure they have
 * their capacity hash value, if not it will generate
 * a hash for the capacity and add it to the capacity.
 *
 * @param availableResources - An array of available resources with accommodations.
 * @returns An object containing the resources with hash mapped, capacities by hash, and an array of hashes.
 */
const getResourcesWithEnforcedCapacityHash = (
    availableResources: ResourceAccommodation[],
): {
    resourcesWithHashMapped: Map<
        ResourceId,
        ResourceAccommodationWithEnforcedCapacityHash
    >;
    capacitiesByHash: Map<CapacityHash, CapacityWithEnforcedHash>;
    hashes: CapacityHash[];
} => {
    // Make sure all capacities have a hash
    // and collect all hashes in an array
    const hashes: CapacityHash[] = [];
    const capacitiesByHash: Map<CapacityHash, CapacityWithEnforcedHash> =
        new Map();
    const resourcesWithHashMapped: Map<
        ResourceId,
        ResourceAccommodationWithEnforcedCapacityHash
    > = new Map<ResourceId, ResourceAccommodationWithEnforcedCapacityHash>();
    // console.time("hashing");
    for (const resource of availableResources) {
        if (!resource.capacity.hash) {
            const hash = capacityHashFactory(resource.capacity);
            resource.capacity.hash = hash;
        }
        hashes.push(resource.capacity.hash);
        resourcesWithHashMapped.set(
            resource._id,
            resource as ResourceAccommodationWithEnforcedCapacityHash,
        );
        if (!capacitiesByHash.has(resource.capacity.hash)) {
            capacitiesByHash.set(
                resource.capacity.hash,
                resource.capacity as CapacityWithEnforcedHash,
            );
        }
    }
    // console.timeEnd("hashing");

    return {
        resourcesWithHashMapped,
        capacitiesByHash,
        hashes,
    };
};

/**
 * Calculates all possible combinations of resource capacities for a given number of resources.
 *
 * @param availableResources - An array of available resources with their capacities.
 * @param numberOfResources - The number of resources to distribute capacities among.
 * @returns An object containing the unique capacity combinations, the total number of combinations,
 *          a map of resources with their capacity hashes, and a map of capacities with their hashes.
 */
const _getPossibleResourceCapacityCombinations = (
    availableResources: ResourceAccommodation[],
    numberOfResources: number,
): {
    uniqueCapacityCombinations: CapacityWithEnforcedHash[][];
    numberOfTotalCombinations: number;
    resourcesWithHashMapped: Map<
        ResourceId,
        ResourceAccommodationWithEnforcedCapacityHash
    >;
    capacitiesByHash: Map<CapacityHash, CapacityWithEnforcedHash>;
} => {
    if (numberOfResources <= 0) return noCombinations;
    if (availableResources.length <= 0) return noCombinations;

    // Make sure all capacities have a hash
    // and collect all hashes in an array
    const {
        hashes,
        capacitiesByHash: capacityByHash,
        resourcesWithHashMapped,
    } = getResourcesWithEnforcedCapacityHash(availableResources);
    const sortedHashes = hashes.sort();

    // Find all possible combinations of capacities for the requested quantity
    // const combinations: ResourceAccommodationWithEnforcedCapacityHash[][] = [];

    let numberOfTotalCombinations = 0;
    // console.log("resources", {
    //     resourcesWithHashCount: resourcesWithHashMapped.size,
    //     requestedNumberOfResources: numberOfResources,
    //     sortedHashes,
    // });

    const limitedSortedHashes: CapacityHash[] = [];
    const hashCounts = new Map<CapacityHash, number>();
    for (const hash of sortedHashes) {
        const count = hashCounts.get(hash) || 0;
        if (count >= numberOfResources) continue;
        limitedSortedHashes.push(hash);
        hashCounts.set(hash, count + 1);
    }
    const uniqueCache = new Set<string>();
    const uniqueCapacityCombinations: CapacityWithEnforcedHash[][] = [];
    const memo = new Map<string, boolean>();

    function combine(
        subset: CapacityWithEnforcedHash[],
        start: number,
        subsetKey: string,
    ) {
        if (subset.length === numberOfResources) {
            // When we reach the requested number of resources
            // in the subset, we'll add it to the found combinations
            numberOfTotalCombinations += 1;
            if (!uniqueCache.has(subsetKey)) {
                uniqueCache.add(subsetKey);
                uniqueCapacityCombinations.push(subset);
                return;
            }
            return;
        }

        if (memo.has(subsetKey)) {
            // console.count(subsetKey);
            return;
        }

        for (let i = start; i < limitedSortedHashes.length; i++) {
            const capacity = capacityByHash.get(limitedSortedHashes[i]);
            if (!capacity) throw new Error("Could not find capacity for hash");
            const newSubsetKey = `${subsetKey}${capacity.hash},`;
            combine([...subset, capacity], i + 1, newSubsetKey);
            memo.set(newSubsetKey, true);
        }
        return;
    }
    // console.time("combinations");
    combine([], 0, "");
    // console.timeEnd("combinations");
    // console.log("COMBINATIONS", {
    //     limitedSortedHashes,
    //     numberOfTotalCombinations,
    //     uniqueCapacityCombinations,
    //     capacitiesByHash: capacityByHash,
    //     resourcesWithHashMapped,
    // });
    return {
        numberOfTotalCombinations,
        resourcesWithHashMapped,
        capacitiesByHash: capacityByHash,
        uniqueCapacityCombinations,
    };
};

type PossibilityResult = {
    firstPossibleCombination: ResourceAccommodationWithEnforcedCapacityHash[];
    isPreferredResourceCombinationPossible: boolean;
    uniqueCapacityCombinations: CapacityWithEnforcedHash[][];
    possibleUniqueCapacityCombinations: CapacityWithEnforcedHash[][];
    possibleResourceIds: Set<ResourceId>;
    capacityHashCountForAvailableResources: Map<CapacityHash, number>;
    errors: QuantityDistributionError[];
    resourcesWithHashMapped: Map<
        ResourceId,
        ResourceAccommodationWithEnforcedCapacityHash
    >;
};

/**
 * Calculates the possibilities of distributing requested quantities
 * among a given number of available resources.
 */
const getPossibilities = (params: {
    availableResources: ResourceAccommodation[];
    /** Prefer to use these resources in the combination if possible, prioritize them if compatible */
    preferredResourceCombination?: ResourceId[];
    requestedQuantities: AccommodationQuantities;
    numberOfResources: number;
    translate?: UseTranslateResult | ValueTranslator;
}): PossibilityResult => {
    const translate = params.translate || svTranslator;
    const errors: QuantityDistributionError[] = [];
    const availableResources = params.availableResources.sort(
        sortAlphabeticallyAndNumerically(r => translate(r.meta.title) || ""),
    );
    const combinations = _getPossibleResourceCapacityCombinations(
        availableResources,
        params.numberOfResources,
    );
    // console.log("COMBINATIONS_RESPONSE", combinations);
    // const matchingCombination: ResourceWithEnforcedCapacityHash[][] = [];
    // let index = 0;
    const availableResourceIds: ResourceId[] = availableResources.map(
        r => r._id,
    );

    // Filter out preferred resources from available resources
    // and filter out the other resources
    const preferredResourcesWithHashes = params.preferredResourceCombination
        ?.map(preferred => {
            const resourceWithHash =
                combinations.resourcesWithHashMapped.get(preferred);
            if (!resourceWithHash) return null;
            return resourceWithHash;
        })
        .filter(exists);
    const preferredResourcesById = new Map<
        ResourceId,
        ResourceAccommodationWithEnforcedCapacityHash
    >(preferredResourcesWithHashes?.map(res => [res._id, res]));
    const preferredCapacityHashes =
        preferredResourcesWithHashes?.map(res => res?.capacity.hash).sort() ??
        [];

    // Count number of available resources for each capacity hash
    const capacityHashCountForAvailableResources = new Map<
        CapacityHash,
        number
    >();
    for (const resource of availableResources) {
        const capacityHash = combinations.resourcesWithHashMapped.get(
            resource._id,
        )?.capacity.hash;
        if (!capacityHash) continue; // should never happen
        const count =
            capacityHashCountForAvailableResources.get(capacityHash) || 0;
        capacityHashCountForAvailableResources.set(capacityHash, count + 1);
    }

    // Put them all in the same list sorted by preferred at top
    // To make sure we find the preferred first
    // const allAvailableResources = [...preferredResources, ...otherResources];
    const uniqueCapacityCombinations = [
        ...combinations.uniqueCapacityCombinations,
    ].sort(
        sortBy(capComb => {
            // We'll sort them by number of available resources with the capacity
            // to try to prefer the ones with the most available resources
            // We'll try to do it using a calculated score of the number of resources available
            // for each capacity in the combination
            const score = sum(
                Array.from(capComb).map(
                    cap =>
                        capacityHashCountForAvailableResources.get(cap.hash) ||
                        0,
                ),
            );
            return -score; // we'll sort by the score, biggest first
        }),
    );

    const uniqueCapacityCombinationsSortedByPreferred =
        preferredCapacityHashes.length
            ? uniqueCapacityCombinations.sort(
                  sortBy(capComb => {
                      // Compare capacity combination with preferred capacity hashes
                      // if it contains all preferred capacity hashes, it should be promoted
                      // to the top
                      const combinationKey = capComb.map(c => c.hash).join(",");
                      const preferredCombinationKey =
                          preferredCapacityHashes.join(",");
                      return combinationKey === preferredCombinationKey
                          ? -1
                          : preferredCapacityHashes.every(ph =>
                                  capComb.map(c => c.hash).includes(ph),
                              )
                            ? -1
                            : 0;
                  }),
              )
            : uniqueCapacityCombinations;

    const picks: ResourceAccommodationWithEnforcedCapacityHash[][] = [];
    const possibleUniqueCapacityCombinations: CapacityWithEnforcedHash[][] = [];
    for (const combination of uniqueCapacityCombinationsSortedByPreferred) {
        // index += 1;
        // Try each combination, if there's an error, catch it and try next
        // if no error is thrown, we now that there's a combination that works
        // return the combinations
        try {
            // console.log(
            //     `👨🏻‍💻 VERIFY COMBINATION ${index}/${uniqueCapacityCombinationsCount}`,
            //     {
            //         requestedQuantities: params.requestedQuantities,
            //         preferredResourcesById,
            //         preferredCapacityHashes,
            //         combination,
            //         combinations,
            //         uniqueCapacityCombinationsSortedByPreferred,
            //     },
            // );
            getDistribution({
                capacities: combination,
                requestedQuantity: params.requestedQuantities,
            });
            possibleUniqueCapacityCombinations.push(combination);
        } catch (error) {
            // console.log(
            //     `💀 VERIFICATION FAILED ${index}/${uniqueCapacityCombinationsCount}`,
            //     error instanceof QuantityDistributionError
            //         ? svTranslator(error.translatableMessage)
            //         : error,
            // );
            if (error instanceof QuantityDistributionError) {
                errors.push(error);
            } else {
                errors.push(
                    new QuantityDistributionError(
                        translatableStringFactory({
                            sv: "Okänt fel uppstod vid kontroll av vald resurskombination",
                            en: "Unknown error occurred when verifying selected resource combination",
                        }),
                        "UNKNOWN_ERROR",
                    ),
                );
            }
            continue;
        }
    }

    // Pick resources for the possible combinations
    for (const combination of possibleUniqueCapacityCombinations) {
        // Find a resource combination that matches the capacity combination
        // there must be one, since we gave picked the unique capacity combinations
        // from the possible combinations

        // Loop through the capacities in the combination, and find the
        // first resource with matching every capacity and
        // matching the hash, and a resource that hasn't been
        // used in the combination
        const usedWithinCombination = new Set<ResourceId>();
        const result: ResourceAccommodationWithEnforcedCapacityHash[] = [];

        for (const capacityOfCombination of combination) {
            // Find first unused resource with the capacity
            // check preferred resources first if there are any
            const preferredResourcesWithHashes =
                params.preferredResourceCombination
                    ?.map(id => combinations.resourcesWithHashMapped.get(id))
                    .filter(exists) ?? [];

            const resource = [
                ...preferredResourcesWithHashes,
                ...availableResources,
            ].find(r => {
                if (usedWithinCombination.has(r._id)) return false;
                const rc = combinations.resourcesWithHashMapped.get(r._id);
                return rc?.capacity.hash === capacityOfCombination.hash;
            });

            const resourceWithCapacity = resource
                ? combinations.resourcesWithHashMapped.get(resource._id)
                : undefined;

            if (resourceWithCapacity) {
                // When we find a resource, we'll mark it
                // as used and place it in the picked array
                usedWithinCombination.add(resourceWithCapacity._id);
                result.push(resourceWithCapacity);
            }
        }

        if (result.length !== combination.length) {
            // This should never happen since we've parsed the possible combinations
            // with capacities from the resources, so there really should be a matching
            // resource and capacity for each capacity in the combination
            throw new Error(
                "Could not find a matching resource for each capacity in the combination",
            );
        }
        // console.log(
        //     `❤️ VERIFIED COMBINATION ${index}/${uniqueCapacityCombinationsCount}`,
        //     {
        //         hash: combination.map(c => c.hash),
        //         resources: result.map(r => r._id),
        //         result,
        //     },
        // );

        picks.push(result);
    }

    // console.log("🐙🐙🐙 UNIQUE_CAPACITY_COMBINATIONS", {
    //     original: combinations.uniqueCapacityCombinations,
    //     preferred: params.preferredResourceCombination,
    //     sorted: uniqueCapacityCombinations,
    //     sortedPreferred: uniqueCapacityCombinationsSortedByPreferred,
    //     possibleUniqueCapacityCombinations,
    //     picks,
    // });

    if (picks.length) {
        const firstPick = picks[0];
        return {
            errors: [],
            firstPossibleCombination: firstPick,
            isPreferredResourceCombinationPossible:
                !preferredResourcesWithHashes?.length ||
                (!!firstPick.length &&
                    firstPick.every(item => {
                        return !!preferredResourcesById.get(item._id);
                    })),
            resourcesWithHashMapped: combinations.resourcesWithHashMapped,
            possibleUniqueCapacityCombinations,
            possibleResourceIds: new Set(
                availableResourceIds.filter(rid => {
                    const r = combinations.resourcesWithHashMapped.get(rid);
                    if (!r) return false;
                    return uniqueCapacityCombinations
                        .flat()
                        .some(c => c.hash === r.capacity.hash);
                }),
            ),
            capacityHashCountForAvailableResources,
            uniqueCapacityCombinations: combinations.uniqueCapacityCombinations,
        };
    }

    return {
        errors,
        firstPossibleCombination: [],
        possibleResourceIds: new Set(),
        possibleUniqueCapacityCombinations: [],
        isPreferredResourceCombinationPossible: false,
        resourcesWithHashMapped: combinations.resourcesWithHashMapped,
        uniqueCapacityCombinations,
        capacityHashCountForAvailableResources,
    };
};

export {
    enforceHashForCapacities,
    enforceHashForCapacity,
    getDistribution,
    getPossibilities,
    getResourcesWithEnforcedCapacityHash,
    QuantityDistributionError,
};
export type { ResourceAccommodationWithEnforcedCapacityHash };
